/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import React from 'react'

import Text from '../../ts-components/Text'

interface StatisticsFiltersProps {
  loading: boolean
  isGenericSport: boolean
  isSoccer: boolean
  isHockey: boolean
  isGoalieStats: boolean
  onPlayerTypeChange: (isGoalie: boolean) => void
  showStatsFilter: boolean
  isGlobalFilterEnabled: boolean
  onGlobalFilterChange: (isGlobal: boolean) => void
}

const StatisticsFilters: React.FC<StatisticsFiltersProps> = ({
  loading,
  isGenericSport,
  isSoccer,
  isHockey,
  isGoalieStats,
  onPlayerTypeChange,
  showStatsFilter,
  isGlobalFilterEnabled,
  onGlobalFilterChange,
}) => {
  return (
    <div
      css={css`
        display: flex;
        gap: 16px;
        align-items: flex-end;

        @media (max-width: 768px) {
          flex-wrap: wrap;
          width: 100%;
        }
      `}
    >
      {!isGenericSport && (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            /* margin-right: 16px; Removed to let gap handle spacing */

            @media (max-width: 768px) {
              width: 100%;
            }
          `}
        >
          <Text
            variant='paragraphSmall'
            weight='regular'
            color={colors.WHITE}
            style={`
              align-self: stretch;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              margin-bottom: 8px;
            `}
          >
            Player Type
          </Text>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 2px;
              background-color: transparent;
              border: 1px solid ${colors.SECONDARY_200};
              border-radius: 8px;
              padding: 4px;
              height: 40px;
              opacity: ${loading ? 0.5 : 1};
              transition: opacity 0.2s ease;
            `}
          >
            <button
              type='button'
              disabled={loading}
              onClick={() => onPlayerTypeChange(false)}
              css={css`
                display: flex;
                align-items: center;
                justify-content: center;
                flex: 1;
                height: 32px;
                border-radius: 6px;
                background-color: ${!isGoalieStats
                  ? colors.SECONDARY_300
                  : 'transparent'};
                border: none;
                cursor: ${loading ? 'not-allowed' : 'pointer'};
                transition: all 0.2s ease;
                font-size: 14px;
                font-weight: ${!isGoalieStats ? 700 : 400};
                line-height: 20.3px;
                color: ${colors.NEUTRAL_0};
                padding: 0 12px;
                white-space: nowrap;

                ${!loading &&
                css`
                  &:hover {
                    background-color: ${!isGoalieStats
                      ? colors.SECONDARY_300
                      : colors.SECONDARY_200};
                  }

                  &:active {
                    transform: scale(0.98);
                  }
                `}
              `}
            >
              {isSoccer || !isHockey ? 'Player' : 'Skater'}
            </button>
            <button
              type='button'
              disabled={loading}
              onClick={() => onPlayerTypeChange(true)}
              css={css`
                display: flex;
                align-items: center;
                justify-content: center;
                flex: 1;
                height: 32px;
                border-radius: 6px;
                background-color: ${isGoalieStats
                  ? colors.SECONDARY_300
                  : 'transparent'};
                border: none;
                cursor: ${loading ? 'not-allowed' : 'pointer'};
                transition: all 0.2s ease;
                font-size: 14px;
                font-weight: ${isGoalieStats ? 700 : 400};
                line-height: 20.3px;
                color: ${colors.NEUTRAL_0};
                padding: 0 12px;
                white-space: nowrap;

                ${!loading &&
                css`
                  &:hover {
                    background-color: ${isGoalieStats
                      ? colors.SECONDARY_300
                      : colors.SECONDARY_200};
                  }

                  &:active {
                    transform: scale(0.98);
                  }
                `}
              `}
            >
              {isSoccer ? 'Keeper' : 'Goalie'}
            </button>
          </div>
        </div>
      )}

      {showStatsFilter && (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            @media (max-width: 768px) {
              width: 100%;
            }
          `}
        >
          <Text
            variant='paragraphSmall'
            weight='regular'
            color={colors.WHITE}
            style={`
              align-self: stretch;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              margin-bottom: 8px;
            `}
          >
            Stats Filter
          </Text>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 2px;
              background-color: transparent;
              border: 1px solid ${colors.SECONDARY_200};
              border-radius: 8px;
              padding: 4px;
              height: 40px;
              opacity: ${loading ? 0.5 : 1};
              transition: opacity 0.2s ease;
            `}
          >
            <button
              type='button'
              disabled={loading}
              onClick={() => onGlobalFilterChange(true)}
              css={css`
                display: flex;
                align-items: center;
                justify-content: center;
                flex: 1;
                height: 32px;
                border-radius: 6px;
                background-color: ${isGlobalFilterEnabled
                  ? colors.SECONDARY_300
                  : 'transparent'};
                border: none;
                cursor: ${loading ? 'not-allowed' : 'pointer'};
                transition: all 0.2s ease;
                font-size: 14px;
                font-weight: ${isGlobalFilterEnabled ? 700 : 400};
                line-height: 20.3px;
                color: ${colors.NEUTRAL_0};
                padding: 0 12px;
                white-space: nowrap;

                ${!loading &&
                css`
                  &:hover {
                    background-color: ${isGlobalFilterEnabled
                      ? colors.SECONDARY_300
                      : colors.SECONDARY_200};
                  }

                  &:active {
                    transform: scale(0.98);
                  }
                `}
              `}
            >
              Season
            </button>
            <button
              type='button'
              disabled={loading}
              onClick={() => onGlobalFilterChange(false)}
              css={css`
                display: flex;
                align-items: center;
                justify-content: center;
                flex: 1;
                height: 32px;
                border-radius: 6px;
                background-color: ${!isGlobalFilterEnabled
                  ? colors.SECONDARY_300
                  : 'transparent'};
                border: none;
                cursor: ${loading ? 'not-allowed' : 'pointer'};
                transition: all 0.2s ease;
                font-size: 14px;
                font-weight: ${!isGlobalFilterEnabled ? 700 : 400};
                line-height: 20.3px;
                color: ${colors.NEUTRAL_0};
                padding: 0 12px;
                white-space: nowrap;

                ${!loading &&
                css`
                  &:hover {
                    background-color: ${!isGlobalFilterEnabled
                      ? colors.SECONDARY_300
                      : colors.SECONDARY_200};
                  }

                  &:active {
                    transform: scale(0.98);
                  }
                `}
              `}
            >
              Division
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default StatisticsFilters
